import {
  faChevronLeft,
  faChevronRight,
  faDownload,
} from '@fortawesome/pro-light-svg-icons';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Select } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import {
  Album,
  ListImage,
  Role,
  useGetImageQuery,
  useTagImageMutation,
  useUntagImageMutation,
} from '../../graphql/schema';
import hasRole from '../../helper/hasRole';

export interface SelectedImage extends ListImage {
  index: number;
}

interface Props {
  image: SelectedImage;
  onClose: () => void;
  next: () => void;
  prev: () => void;
  album: Album;
  isLast: boolean;
}

const ImagePreview = ({ image, onClose, prev, next, album, isLast }: Props) => {
  const tagInputRef = useRef<any>(null);
  const { user } = useAuth();

  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  const [tag] = useTagImageMutation();
  const [untag] = useUntagImageMutation();

  const editModeActive = useMemo(() => {
    return (
      hasRole(user, [Role.ADMIN]) ||
      (hasRole(user, [Role.USER]) &&
        album.userIds.includes(user?.profile.sub ?? ''))
    );
  }, [user, album]);

  const { data } = useGetImageQuery({
    variables: {
      albumId: album.id,
      imageId: image.id,
    },
    skip: !editModeActive,
  });

  useEffect(() => {
    setSelectedTagIds(data?.image.tagsIds ?? []);
  }, [data]);

  useEffect(() => {
    const handleKeyDown = (e: any) => {
      if (e.keyCode === 27) {
        onClose();
        return;
      }
      if (e.keyCode === 37) {
        prev();
        return;
      }
      if (e.keyCode === 39) {
        next();
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, prev, next]);

  useEffect(() => {
    if (editModeActive) {
      tagInputRef.current?.blur();
      setTimeout(() => {
        tagInputRef.current?.focus();
      }, 100);
    }
  }, [image, editModeActive]);

  const handleChangeTags = useCallback(
    (tagIds: string[]) => {
      setSelectedTagIds((oldTagIds) => {
        // Find added tags
        for (const tagId of tagIds) {
          if (!oldTagIds.includes(tagId)) {
            tag({
              variables: {
                albumId: album.id,
                imageId: image.id,
                tagId,
              },
            });
          }
        }

        for (const tagId of oldTagIds) {
          if (!tagIds.includes(tagId)) {
            untag({
              variables: {
                albumId: album.id,
                imageId: image.id,
                tagId,
              },
            });
          }
        }

        return tagIds;
      });
    },
    [album, image],
  );

  const handleDownload = useCallback(() => {
    if (!image.url) return;

    fetch(image.url)
      .then((response) => response.blob())
      .then((blob) => {
        const url = URL.createObjectURL(new Blob([blob]));
        const link = document.createElement<'a'>('a');
        link.href = url;
        link.download = image.fileName ?? 'image.jpg';
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(url);
        link.remove();
      });
  }, [image]);

  return (
    <div className="fixed left-0 top-0 right-0 bottom-0 bg-black bg-opacity-70 flex justify-center items-center">
      <img
        src={image.screenUrl ?? ''}
        alt=""
        className="max-w-[80%] max-h-[80%]"
      />
      {image.index > 0 && (
        <div
          className="text-white absolute top-0 left-0 h-full p-5 cursor-pointer flex justify-center items-center"
          onClick={prev}
        >
          <FontAwesomeIcon icon={faChevronLeft} size="2xl" />
        </div>
      )}
      {!isLast && (
        <div
          className="text-white absolute top-0 right-0 h-full p-5 cursor-pointer flex justify-center items-center"
          onClick={next}
        >
          <FontAwesomeIcon icon={faChevronRight} size="2xl" />
        </div>
      )}
      {editModeActive && data?.image && (
        <div className="absolute flex bottom-1 left-0 right-0 items-center justify-center space-x-2">
          <div className="text-white">Tags:</div>
          <Select
            className="w-1/4"
            mode="multiple"
            optionFilterProp="label"
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? '')
                .toLowerCase()
                .localeCompare((optionB?.label ?? '').toLowerCase())
            }
            placeholder="Tags"
            onChange={handleChangeTags}
            options={album.tags.map((tag) => ({
              value: tag.id,
              label: tag.name,
            }))}
            autoClearSearchValue
            ref={tagInputRef}
            value={selectedTagIds}
          />
        </div>
      )}
      <div className="absolute top-1 right-1 space-x-5 flex flex-row">
        <div className="text-white p-5 cursor-pointer" onClick={handleDownload}>
          <FontAwesomeIcon icon={faDownload} size="xl" />
        </div>
        <div className="text-white p-5 cursor-pointer" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} size="2xl" />
        </div>
      </div>
    </div>
  );
};

export default ImagePreview;
